import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { V } from '../../common/Layout';
import { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import type { Entity } from '../constants/customSortFieldConfigs';
import { FormProvider } from '../form/FormContext';
import RenderLexicalEditor from '../form/LexicalTemplateEditor';
import { useBrowserOrAccountLocale } from '../hooks/useBrowserOrAccountLocale';
import { failedToastMessage } from '../utils/commonUtils';
import type { EntityReportAdapter, ReportTemplateDataValue, TemplateStrategy } from './adapters/EntityReportAdapter';
import { extractReportConfigurations, resolveTemplate } from './reportUtils';

export default function StepPreviewReport<TRawRow extends Record<string, unknown> = Record<string, unknown>>({
    latestSearchResults,
    previewData,
    sortOrders,
    onRegisterExport,
    entityReportAdapter,
    preferenceEntityKey,
    reportContextTerm,
    reportTitle
}: {
    latestSearchResults?: TRawRow[];
    previewData?: ReportTemplateDataValue;
    sortOrders?: (string | number)[][];
    onRegisterExport?: (fn?: () => Promise<void>) => void;
    entityReportAdapter: EntityReportAdapter<TRawRow>;
    preferenceEntityKey: string;
    reportContextTerm?: string;
    reportTitle: string;
}) {
    const { getStepData, activeStep, setStepData, setActiveStep } = useStepper();
    const locale = useBrowserOrAccountLocale();
    const reportTemplateStep = getStepData(StepsIndexConstant.zero);
    const configureStep = getStepData(StepsIndexConstant.one);
    const entity = entityReportAdapter.entity as Entity;
    const currentData = getStepData(activeStep);
    const customizeStep = getStepData(StepsIndexConstant.two);
    const reportConfigurations = useMemo(
        () => extractReportConfigurations(configureStep || undefined, entity),
        [configureStep, entity]
    );

    const resolvedTemplateData = useMemo((): {
        data: Record<string, any> | Record<string, any>[];
        strategy: TemplateStrategy;
    } => {
        if (entityReportAdapter.templateStrategy === 'documentContext') {
            return {
                data: entityReportAdapter.buildTemplateContext({
                    previewData,
                    latestSearchResults,
                    locale
                }),
                strategy: 'documentContext'
            };
        }

        const sourceRows = (latestSearchResults ?? []) as TRawRow[];
        return {
            data: entityReportAdapter.normalizeRows(sourceRows, locale),
            strategy: 'tableRows'
        };
    }, [entityReportAdapter, latestSearchResults, locale, previewData]);

    const resolvedTemplateResult = useMemo(() => {
        const base = customizeStep?.rich;
        if (!base) {
            return { template: currentData?.rich ?? null, error: null };
        }

        try {
            return {
                template: resolveTemplate(
                    base,
                    resolvedTemplateData.data,
                    locale,
                    resolvedTemplateData.strategy,
                    entity,
                    reportConfigurations,
                    resolvedTemplateData.strategy === 'tableRows' ? sortOrders : undefined
                ),
                error: null
            };
        } catch (err) {
            return {
                template: currentData?.rich ?? customizeStep?.rich ?? null,
                error: err
            };
        }
    }, [
        currentData?.rich,
        customizeStep?.rich,
        resolvedTemplateData,
        reportConfigurations,
        locale,
        sortOrders,
        entity
    ]);

    useEffect(() => {
        if (!resolvedTemplateResult.error) {
            return;
        }

        failedToastMessage('report.load.failed', resolvedTemplateResult.error);
        setActiveStep(StepsIndexConstant.two);
    }, [resolvedTemplateResult.error, setActiveStep]);

    const [values, setValues] = useState<Record<string, any>>({
        rich: resolvedTemplateResult.template
    });

    useEffect(() => {
        if (!isEqual(currentData, values)) {
            setStepData(activeStep, values);
        }
    }, [currentData, values, activeStep, setStepData]);

    return (
        <FormProvider validationRules={undefined} state={values} setState={setValues} allowMultipleExpansion>
            <V fill sx={{ p: 2 }}>
                <RenderLexicalEditor
                    isPreview
                    reportTitle={reportTitle || reportTemplateStep.reportTitle}
                    onRegisteredExport={onRegisterExport}
                    entity={entity}
                    preferenceEntityKey={preferenceEntityKey}
                    reportContextTerm={reportContextTerm}
                />
            </V>
        </FormProvider>
    );
}
